import { useEffect } from "react";
import { imageurl } from "../../App";
import { applyStoreTitle, connectSeoText } from "../../util/storeLogoHelper";
import { loadStoreSettings } from "../../util/storeSettings";

export const getCachedStoreSettings = loadStoreSettings;

/**
 * Utility to strip HTML tags from strings and normalize whitespace
 */
export const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
};

/**
 * Clean & truncate description to optimal SEO length (155-160 chars)
 */
export const formatSeoDescription = (text, maxLength = 160) => {
  const clean = stripHtml(text);
  if (clean.length <= maxLength) return clean;
  const truncated = clean.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(" ");
  return (lastSpace > 110 ? truncated.substring(0, lastSpace) : truncated) + "...";
};

/**
 * Helper to update or create a <meta> tag by name or property
 */
const setMetaTag = (attributeName, attributeValue, content) => {
  if (content === undefined || content === null) return;
  let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

/**
 * Helper to update or create a <link> tag
 */
const setLinkTag = (rel, href) => {
  if (!href) return;
  let element = document.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
};

/**
 * Helper to inject or update JSON-LD Structured Data script
 */
const setJsonLd = (id, jsonObject) => {
  let script = document.getElementById(id);
  if (!jsonObject) {
    if (script) script.remove();
    return;
  }
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.text = JSON.stringify(jsonObject);
};

/**
 * Format complete image URL
 */
const resolveFullImageUrl = (img, baseUrl) => {
  if (!img) return "";
  if (img.startsWith("http://") || img.startsWith("https://")) return img;
  const cleanImg = img.startsWith("/") ? img.substring(1) : img;
  if (cleanImg.startsWith("uploads/")) {
    return `${imageurl}${cleanImg}`;
  }
  return `${baseUrl}/${cleanImg}`;
};

/**
 * SEOHead: Shopify-grade Dynamic On-Page SEO Component
 */
export default function SEOHead({
  title,
  description,
  keywords,
  image,
  url,
  type = "website",
  product = null,
  category = null,
  breadcrumbs = null,
  noindex = false,
}) {
  useEffect(() => {
    let isCurrent = true;

    getCachedStoreSettings().then((storeSettings) => {
      if (!isCurrent) return;

      const storeName = (storeSettings.storeName || "").trim() || "Store";
      const separator = storeSettings.titleSeparator || "–";
      const defaultDesc = connectSeoText(
        storeSettings.metaDescription ||
          `Shop ${storeName} online with fast delivery and cash on delivery.`,
        storeName
      );
      const defaultKeywords = connectSeoText(
        storeSettings.metaKeywords ||
          `${storeName}, online shopping, best deals, cash on delivery`,
        storeName
      );
      const robotsIndexing = storeSettings.robotsIndexing || "index, follow";

      // 1. Determine Origin / Canonical Base
      const baseUrl =
        storeSettings.canonicalBaseUrl && storeSettings.canonicalBaseUrl.trim() !== ""
          ? storeSettings.canonicalBaseUrl.replace(/\/+$/, "")
          : window.location.origin;

      const pathOnly = url
        ? `${url.startsWith("/") ? "" : "/"}${url.split("?")[0]}`
        : window.location.pathname;
      const fullUrl = `${baseUrl}${pathOnly}`;
      const previewing = new URLSearchParams(window.location.search).has("preview_theme");

      // 2. Resolve Meta Title (Shopify Fallback Template)
      let pageLabel = "";
      if (title && title.trim()) {
        pageLabel = title.trim();
      } else if (product?.name) {
        const brandPart = product.brand ? ` ${separator} ${product.brand}` : "";
        pageLabel = `${product.name}${brandPart}`;
      } else if (category?.name) {
        pageLabel = `${category.name} Collection`;
      }
      applyStoreTitle(storeSettings, pageLabel);
      const finalTitle = document.title;

      // 3. Resolve Meta Description
      let rawDescription = description;
      if (!rawDescription) {
        if (product) {
          rawDescription =
            product.meta_descrip ||
            product.discription ||
            product.description ||
            `Buy ${product.name} online at ${storeName}. Best price in Pakistan with fast nationwide delivery & COD.`;
        } else if (category) {
          rawDescription =
            category.meta_descrip ||
            category.description ||
            `Explore our premium ${category.name} collection at ${storeName}. Best deals with cash on delivery.`;
        } else {
          rawDescription = defaultDesc;
        }
      }
      const finalDescription = formatSeoDescription(connectSeoText(rawDescription, storeName));

      // 4. Resolve Meta Keywords
      let finalKeywords = keywords;
      if (!finalKeywords) {
        if (product) {
          finalKeywords =
            product.meta_keyword ||
            `${product.name}, ${product.brand || ""}, ${category?.name || ""}, ${storeName}, buy online, best price, cash on delivery`;
        } else if (category) {
          finalKeywords =
            category.meta_keyword ||
            `${category.name}, ${category.name} collection, ${storeName}, cash on delivery`;
        } else {
          finalKeywords = defaultKeywords;
        }
      }
      finalKeywords = connectSeoText(finalKeywords, storeName);

      // 5. Resolve Social Share Image (OG Image)
      let finalImage = image;
      if (!finalImage) {
        if (product) {
          const firstImg =
            product.product_image?.[0]?.image || product.image || "";
          finalImage = resolveFullImageUrl(firstImg, baseUrl);
        } else if (category?.image) {
          finalImage = resolveFullImageUrl(category.image, baseUrl);
        }
      }
      if (!finalImage && storeSettings.socialShareImage) {
        finalImage = resolveFullImageUrl(storeSettings.socialShareImage, baseUrl);
      }
      if (!finalImage && storeSettings.logoUrl) {
        finalImage = resolveFullImageUrl(storeSettings.logoUrl, baseUrl);
      }

      // 6. Invert/Set Standard Head Tags
      setMetaTag("name", "description", finalDescription);
      setMetaTag("name", "keywords", finalKeywords);

      // Robots Tag
      const finalRobots = noindex || previewing ? "noindex, nofollow" : robotsIndexing;
      setMetaTag("name", "robots", finalRobots);

      // Canonical Link
      setLinkTag("canonical", fullUrl);

      // 7. OpenGraph Social Meta Tags
      setMetaTag("property", "og:site_name", storeName);
      setMetaTag("property", "og:title", finalTitle);
      setMetaTag("property", "og:description", finalDescription);
      setMetaTag("property", "og:url", fullUrl);
      setMetaTag("property", "og:type", type === "product" ? "product" : "website");
      if (finalImage) {
        setMetaTag("property", "og:image", finalImage);
      }

      // E-commerce specific OpenGraph tags
      if (product) {
        const price =
          product.pricing?.final_price ??
          product.productprices?.seeling_price ??
          product.selling_price ??
          0;
        const currency = storeSettings.currency || "PKR";
        const inStock =
          product.pricing?.in_stock ??
          ((product.productprices?.qty ?? product.qty ?? 0) > 0);

        setMetaTag("property", "product:price:amount", String(price));
        setMetaTag("property", "product:price:currency", currency);
        setMetaTag(
          "property",
          "product:availability",
          inStock ? "instock" : "oos"
        );
      }

      // 8. Twitter Card Meta Tags
      setMetaTag("name", "twitter:card", "summary_large_image");
      setMetaTag("name", "twitter:title", finalTitle);
      setMetaTag("name", "twitter:description", finalDescription);
      if (finalImage) {
        setMetaTag("name", "twitter:image", finalImage);
      }

      // 9. Webmaster Verification Codes (from Store Config)
      if (storeSettings.googleSiteVerification) {
        setMetaTag(
          "name",
          "google-site-verification",
          storeSettings.googleSiteVerification
        );
      }
      if (storeSettings.bingSiteVerification) {
        setMetaTag("name", "msvalidate.01", storeSettings.bingSiteVerification);
      }

      // 10. Schema.org JSON-LD Structured Data
      const richSnippetsEnabled = storeSettings.enableRichSnippets !== false;

      if (richSnippetsEnabled) {
        const schemaGraph = [];

        // Organization / Store Schema
        schemaGraph.push({
          "@type": "OnlineStore",
          "@id": `${baseUrl}/#store`,
          name: storeName,
          url: baseUrl,
          description: defaultDesc,
          logo: storeSettings.logoUrl
            ? resolveFullImageUrl(storeSettings.logoUrl, baseUrl)
            : undefined,
          sameAs: [
            storeSettings.facebookUrl,
            storeSettings.instagramUrl,
            storeSettings.twitterUrl,
            storeSettings.linkedinUrl,
          ].filter(Boolean),
        });

        // WebSite Schema with Sitelinks Searchbox
        schemaGraph.push({
          "@type": "WebSite",
          "@id": `${baseUrl}/#website`,
          url: baseUrl,
          name: storeName,
          potentialAction: {
            "@type": "SearchAction",
            target: `${baseUrl}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string",
          },
        });

        // Breadcrumbs Schema
        const breadcrumbItems = [];
        breadcrumbItems.push({
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${baseUrl}/`,
        });

        if (category) {
          breadcrumbItems.push({
            "@type": "ListItem",
            position: 2,
            name: category.name,
            item: `${baseUrl}/collection/${category.slug}`,
          });
        }

        if (product) {
          const catPos = category ? 3 : 2;
          breadcrumbItems.push({
            "@type": "ListItem",
            position: catPos,
            name: product.name,
            item: fullUrl,
          });
        } else if (breadcrumbs && Array.isArray(breadcrumbs)) {
          breadcrumbs.forEach((bc, idx) => {
            breadcrumbItems.push({
              "@type": "ListItem",
              position: idx + 2,
              name: bc.name,
              item: bc.url.startsWith("http") ? bc.url : `${baseUrl}${bc.url}`,
            });
          });
        }

        if (breadcrumbItems.length > 1) {
          schemaGraph.push({
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbItems,
          });
        }

        // Product Schema
        if (product) {
          const price =
            product.pricing?.final_price ??
            product.productprices?.seeling_price ??
            product.selling_price ??
            0;
          const currency = storeSettings.currency || "PKR";
          const inStock =
            product.pricing?.in_stock ??
            ((product.productprices?.qty ?? product.qty ?? 0) > 0);

          const productImages = [];
          if (product.product_image && Array.isArray(product.product_image)) {
            product.product_image.forEach((img) => {
              if (img?.image) productImages.push(resolveFullImageUrl(img.image, baseUrl));
            });
          }
          if (productImages.length === 0 && finalImage) {
            productImages.push(finalImage);
          }

          const productSchema = {
            "@type": "Product",
            "@id": `${fullUrl}#product`,
            name: product.name,
            image: productImages,
            description: finalDescription,
            offers: {
              "@type": "Offer",
              url: fullUrl,
              priceCurrency: currency,
              price: String(price),
              itemCondition: "https://schema.org/NewCondition",
              availability: inStock
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
              seller: {
                "@type": "Organization",
                name: storeName,
              },
            },
          };
          if (product.brand) {
            productSchema.brand = { "@type": "Brand", name: product.brand };
          }

          schemaGraph.push(productSchema);
        }

        // Inject compiled graph
        setJsonLd("seo-json-ld", {
          "@context": "https://schema.org",
          "@graph": schemaGraph,
        });
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [
    title,
    description,
    keywords,
    image,
    url,
    type,
    product,
    category,
    breadcrumbs,
    noindex,
  ]);

  return null; // Pure head injector
}
